import { useState, useEffect } from 'react';

/**
 * Custom hook that debounces any fast-changing value (e.g. search query input).
 * Delays updating the debounced value until after the specified delay has elapsed
 * since the last change, preventing excessive server-side requests during typing.
 *
 * @param value The value to debounce
 * @param delay Milliseconds to delay (default 400ms)
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number = 400): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}
